import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { Dispatch, SetStateAction } from 'react';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
} from 'react-beautiful-dnd';
import { ContentKeywordGq } from '../../../../../graphql/projects/models/ContentKeywordGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import ContentKeywordAddTableRow from './ContentKeywordAddTableRow';
import { ContentKeywordSearchTableState } from './logic/useContentKeywordSearchTableState';

export interface ContentKeywordAddTableProps {
  clientUrl: string;
  project: ProjectGq;
  localAddKeywords: ContentKeywordGq[];
  debouncedSearchTerm: string;
  setPaging: Dispatch<SetStateAction<Object>>;
  keywordQueryRows: number;
  keywordQueryPage: number;
  keywordQueryCount: number;
  keywordQueryLoading: boolean;
  isDragginGlobal?: boolean;
  tableState: ContentKeywordSearchTableState;
  selectBag: {
    deactivateDragMode: () => void;
    onMouseOver: (keywordIndex: number) => void;
    onMouseDown: (contentKeywordId: number, keywordIndex: number) => void;
    isIndexSelected: (index: number) => boolean;
  };
}

export const ContentKeywordAddTable: React.FC<ContentKeywordAddTableProps> = ({
  clientUrl,
  localAddKeywords,
  debouncedSearchTerm,
  setPaging,
  keywordQueryRows,
  keywordQueryPage,
  keywordQueryCount,
  keywordQueryLoading,
  isDragginGlobal,
  tableState,
  selectBag,
  project,
}) => {
  return (
    <div className={'flex flex-col flex-nowrap min-h-[304px]'}>
      <TableContainer>
        {/* We use div components because droppable is a div and wouldn't be valid otherwise */}
        <Table size="small" component="div">
          {!!debouncedSearchTerm.length && !!localAddKeywords?.length && (
            <TableHead component="div">
              <TableRow component="div">
                <TableCell padding={'none'} component="div"></TableCell>
                <TableCell component="div">Keyword</TableCell>
                {project.hasEnglishTranslation && <TableCell component="div">English</TableCell>}
                <TableCell component="div" align="right">
                  Volume
                </TableCell>
                <TableCell component="div" align="right" className="whitespace-no-wrap">
                  Current Rank
                </TableCell>
                <TableCell component="div" className="whitespace-no-wrap">
                  Current Url
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <Droppable droppableId={'addTable'} direction="vertical" isDropDisabled={true}>
            {(droppableProvided: DroppableProvided) => (
              <TableBody
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                component="div"
              >
                {!localAddKeywords?.length && !!debouncedSearchTerm.length && !keywordQueryLoading && (
                  <Box p={2} fontStyle="oblique">
                    <Typography variant={'body2'}>No keywords found</Typography>
                  </Box>
                )}
                {localAddKeywords?.map((contentKeyword, index) => (
                  <Draggable
                    key={contentKeyword.keywordId}
                    draggableId={contentKeyword.keywordId.toString()}
                    index={index}
                  >
                    {(
                      draggaBleProvided: DraggableProvided,
                      draggableSnapshot: DraggableStateSnapshot
                    ) => {
                      return (
                        <ContentKeywordAddTableRow
                          key={contentKeyword.id}
                          project={project}
                          contentKeyword={contentKeyword}
                          clientUrl={clientUrl}
                          provided={draggaBleProvided}
                          snapshot={draggableSnapshot}
                          isDragginGlobal={isDragginGlobal}
                          tableState={tableState}
                          selectBag={selectBag}
                          index={index}
                        />
                      );
                    }}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </TableContainer>
      {!!debouncedSearchTerm.length && keywordQueryCount / keywordQueryRows > 1 && (
        <TablePagination
          component={'div'}
          page={keywordQueryPage ?? 0}
          onPageChange={(value, page) => setPaging((prev) => ({ ...prev, page }))}
          rowsPerPage={keywordQueryRows ?? 10}
          onRowsPerPageChange={(value) =>
            setPaging((prev) => ({ ...prev, rowsPerPage: +value.target.value }))
          }
          count={keywordQueryCount ?? 0}
          rowsPerPageOptions={[5]}
        />
      )}
    </div>
  );
};

export default ContentKeywordAddTable;
